import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useMutation } from '@tanstack/react-query';
import { useAppContext } from 'components/AppContext';
import * as api from 'utils/api';
import surveyImg from 'assets/images/survey-img.png';
import styles from './Survey.module.scss';

const STEPS = {
  JOB_SELECTION: 1,
  PLATFORM_DISCOVERY: 2,
};

const jobOptions = [
  { value: 'Agent', label: 'Agent' },
  { value: 'Team Leader', label: 'Team Leader' },
  { value: 'Broker', label: 'Broker' },
  { value: 'Property manager', label: 'Property manager' },
  { value: 'Real estate investor', label: 'Real estate investor' },
  { value: 'Real estate marketer', label: 'Real estate marketer' },
  { value: 'Real estate developer', label: 'Real estate developer' },
  { value: 'Professional stager', label: 'Professional stager' },
  { value: 'Real estate photographer', label: 'Real estate photographer' },
  { value: 'Lease/Leasing company', label: 'Lease/Leasing company' },
  { value: 'Interior designer', label: 'Interior designer' },
  { value: 'custom', label: 'Add your work' },
];

const platformOptions = [
  { value: 'Google', label: 'Google' },
  { value: 'Bing', label: 'Bing' },
  { value: 'Tiktok', label: 'Tiktok' },
  { value: 'Instagram', label: 'Instagram' },
  { value: 'Email/SMS', label: 'Email/SMS' },
  { value: 'Word of mouth', label: 'Word of mouth' },
  { value: 'Twitter', label: 'Twitter' },
  { value: 'Online advertisement', label: 'Online advertisement' },
  { value: 'Facebook', label: 'Facebook' },
  { value: 'KOL', label: 'KOL' },
  { value: 'Discord', label: 'Discord' },
  { value: 'custom', label: 'Add your answer...' },
];

export default function Survey({ onClose }) {
  const [currentStep, setCurrentStep] = useState(STEPS.JOB_SELECTION);
  const [selectedJob, setSelectedJob] = useState('');
  const [customJob, setCustomJob] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [customPlatform, setCustomPlatform] = useState('');
  const { getInfo } = useAppContext();
  const customJobInputRef = useRef(null);
  const customPlatformInputRef = useRef(null);

  const { mutateAsync: updateUserInfo, isLoading } = useMutation({
    mutationFn: (data) => api.updateInfo(data),
    onSuccess: () => {
      getInfo();
      onClose();
    },
  });

  const handleJobSelect = (job) => {
    setSelectedJob(job);
    if (job !== 'custom') {
      setCustomJob('');
    }
  };

  // Focus the custom job input when the custom option is selected
  useEffect(() => {
    if (selectedJob === 'custom' && customJobInputRef.current) {
      customJobInputRef.current.focus();
    }
  }, [selectedJob]);

  // Focus the custom platform input when the custom option is selected
  useEffect(() => {
    if (selectedPlatform === 'custom' && customPlatformInputRef.current) {
      customPlatformInputRef.current.focus();
    }
  }, [selectedPlatform]);

  const handlePlatformSelect = (platform) => {
    setSelectedPlatform(platform);
    if (platform !== 'custom') {
      setCustomPlatform('');
    }
  };

  const handleNext = () => {
    if (currentStep === STEPS.JOB_SELECTION) {
      setCurrentStep(STEPS.PLATFORM_DISCOVERY);
    }
  };

  const handleSubmit = async () => {
    const job = selectedJob === 'custom' ? customJob : selectedJob;
    const fromPlatform = selectedPlatform === 'custom' ? customPlatform : selectedPlatform;

    await updateUserInfo({
      job,
      fromPlatform,
    });
  };

  const isJobStepValid = selectedJob && (selectedJob !== 'custom' || customJob.trim());
  const isPlatformStepValid = selectedPlatform && (selectedPlatform !== 'custom' || customPlatform.trim());

  return (
    <Modal
      show
      onHide={onClose}
      backdrop="static"
      size="lg"
      centered
      className={styles.surveyModal}
    >

      <Modal.Body className={styles.modalBody}>
        <div className={styles.stepContent}>
          <h2 className={styles.stepTitle}>
            {currentStep === STEPS.JOB_SELECTION ? 'What type of work do you do?' : 'How did you hear about us?'}
          </h2>

          {/* Step indicator */}
          <div className={styles.stepIndicator}>
            <div className={`${styles.stepBar} ${currentStep === STEPS.JOB_SELECTION ? styles.active : styles.completed}`} />
            <div className={`${styles.stepBar} ${currentStep === STEPS.PLATFORM_DISCOVERY ? styles.active : ''}`} />
          </div>
          {currentStep === STEPS.JOB_SELECTION && (
            <>
              <div className={styles.optionsGrid}>
                {jobOptions.slice(0, -1).map((option) => (
                  <div
                    key={option.value}
                    className={`${styles.optionWrapper} ${selectedJob === option.value ? styles.selected : ''}`}
                    onClick={() => handleJobSelect(option.value)}
                  >
                    <div className={styles.customRadio}>
                      {selectedJob === option.value && <div className={styles.radioInner} />}
                    </div>
                    <span className={styles.optionLabel}>
                      {option.label}
                    </span>
                  </div>
                ))}

                {/* Custom input as the last option */}
                <div
                  className={`${styles.customJobWrapper} ${selectedJob === 'custom' ? styles.selected : ''}`}
                  onClick={() => handleJobSelect('custom')}
                >
                  <div className={styles.customRadio}>
                    {selectedJob === 'custom' && <div className={styles.radioInner} />}
                  </div>
                  <Form.Control
                    ref={customJobInputRef}
                    type="text"
                    placeholder="Add your work"
                    value={customJob}
                    onChange={(e) => {
                      setCustomJob(e.target.value);
                      handleJobSelect('custom');
                    }}
                    onFocus={() => handleJobSelect('custom')}
                    onClick={(e) => e.stopPropagation()}
                    className={styles.customJobInput}
                  />
                </div>
              </div>
            </>
          )}

          {currentStep === STEPS.PLATFORM_DISCOVERY && (
            <>
              <div className={styles.optionsGrid}>
                {platformOptions.slice(0, -1).map((option) => (
                  <div
                    key={option.value}
                    className={`${styles.optionWrapper} ${selectedPlatform === option.value ? styles.selected : ''}`}
                    onClick={() => handlePlatformSelect(option.value)}
                  >
                    <div className={styles.customRadio}>
                      {selectedPlatform === option.value && <div className={styles.radioInner} />}
                    </div>
                    <span className={styles.optionLabel}>
                      {option.label}
                    </span>
                  </div>
                ))}

                {/* Custom input as the last option */}
                <div
                  className={`${styles.customJobWrapper} ${selectedPlatform === 'custom' ? styles.selected : ''}`}
                  onClick={() => handlePlatformSelect('custom')}
                >
                  <div className={styles.customRadio}>
                    {selectedPlatform === 'custom' && <div className={styles.radioInner} />}
                  </div>
                  <Form.Control
                    ref={customPlatformInputRef}
                    type="text"
                    placeholder="Add your answer..."
                    value={customPlatform}
                    onChange={(e) => {
                      setCustomPlatform(e.target.value);
                      handlePlatformSelect('custom');
                    }}
                    onFocus={() => handlePlatformSelect('custom')}
                    onClick={(e) => e.stopPropagation()}
                    className={styles.customJobInput}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {/* Shared image container for both steps */}
        <div className={styles.imageContainer}>
          <img
            src={surveyImg}
            alt="survey"
          />
        </div>
      </Modal.Body>

      <Modal.Footer className={styles.modalFooter}>
        {currentStep === STEPS.JOB_SELECTION && (
          <Button
            variant="primary"
            onClick={handleNext}
            disabled={!isJobStepValid}
            className={styles.nextButton}
          >
            Next
            <svg className={styles.arrowIcon} viewBox="0 0 24 24" fill="none">
              <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Button>
        )}

        {currentStep === STEPS.PLATFORM_DISCOVERY && (
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!isPlatformStepValid || isLoading}
            className={styles.doneButton}
          >
            {isLoading ? 'Saving...' : 'Done'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
